// Naming the period a payment request covers, from the created-date range the service resolved.
//
// A range that lines up exactly with a calendar unit is named as that unit — "June invoices",
// "Q2 invoices", "Invoices from 2025" — because that is how the user asked for it and it describes the
// same fact more plainly than its end dates do. Anything else reads as its dates. Invoice numbers never
// appear here: a period is described by what was ASKED for, not by what it resolved to.
//
// The BASIS always shows, because invoices RAISED in a period and invoices DUE in it are different sets.
// Reading "June invoices" for a due-date ask would name the wrong thing entirely, so a due period is
// always phrased as due.

// A calendar day (yyyy-MM-dd), built at LOCAL midnight. `new Date("2026-06-01")` is parsed as UTC
// midnight, which is the previous day for anyone west of UTC — so getDate() would report 31 May and
// every day-of-month comparison below would be wrong outside Europe.
function parseDay(iso) {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  if (!parts) return null;
  const [year, month, day] = parts.slice(1).map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function lastDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

const isFirstOfMonth = (date) => date.getDate() === 1;
const isLastOfMonth = (date) => date.getDate() === lastDayOfMonth(date);
const isSameYear = (from, to) => from.getFullYear() === to.getFullYear();

function dayMonth(date) {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

const DUE = "Due";

// A raised period leads with the unit ("June invoices"); a due period has to say "due", so it leads with
// the noun instead ("Invoices due in June"). The year is named only when it is not the year the facts
// were resolved in — "June invoices" would otherwise be ambiguous for last June.
function qualified(unit, from, asAt, basis) {
  const named =
    asAt && from.getFullYear() === asAt.getFullYear() ? unit : `${unit} ${from.getFullYear()}`;
  return basis === DUE ? `Invoices due in ${named}` : `${named} invoices`;
}

function wholeYear(from, to, asAt, basis) {
  const spansTheYear =
    isSameYear(from, to) && from.getMonth() === 0 && isFirstOfMonth(from)
    && to.getMonth() === 11 && isLastOfMonth(to);
  if (!spansTheYear) return null;

  // "2026 invoices" would read as a COUNT of invoices, so a year is always phrased the other way round.
  const year = from.getFullYear();
  return basis === DUE ? `Invoices due in ${year}` : `Invoices from ${year}`;
}

function wholeQuarter(from, to, asAt, basis) {
  const startsAQuarter = from.getMonth() % 3 === 0 && isFirstOfMonth(from);
  const spansThreeMonths = isSameYear(from, to) && to.getMonth() === from.getMonth() + 2;
  return startsAQuarter && spansThreeMonths && isLastOfMonth(to)
    ? qualified(`Q${from.getMonth() / 3 + 1}`, from, asAt, basis)
    : null;
}

function wholeMonth(from, to, asAt, basis) {
  const spansOneMonth = isSameYear(from, to) && from.getMonth() === to.getMonth();
  return spansOneMonth && isFirstOfMonth(from) && isLastOfMonth(to)
    ? qualified(from.toLocaleDateString("en-GB", { month: "long" }), from, asAt, basis)
    : null;
}

// "1–7 Aug" within one month, "28 Jul – 3 Aug" across two, and an open end when only one date is known.
// A due period is prefixed so it cannot be mistaken for a raised one.
function rangeText(from, to, basis) {
  const dates = plainRange(from, to);
  if (!dates) return null;
  return basis === DUE ? `Due ${dates}` : dates;
}

function plainRange(from, to) {
  if (from && to) {
    return isSameYear(from, to) && from.getMonth() === to.getMonth()
      ? `${from.getDate()}–${to.getDate()} ${to.toLocaleDateString("en-GB", { month: "short" })}`
      : `${dayMonth(from)} – ${dayMonth(to)}`;
  }
  if (from) return `from ${dayMonth(from)}`;
  if (to) return `up to ${dayMonth(to)}`;
  return null;
}

// Widest calendar unit first; each returns null when the range does not line up with it.
const CALENDAR_UNITS = [wholeYear, wholeQuarter, wholeMonth];

// The period's name, or null when there are no dates to name it by. `basis` is "Raised" or "Due" — which
// of the invoice's dates the range was measured against.
export function invoicePeriodText(periodFrom, periodTo, asAt, basis) {
  const from = parseDay(periodFrom);
  const to = parseDay(periodTo);
  if (!from || !to) return rangeText(from, to, basis);

  const asAtDay = parseDay(asAt);
  for (const unit of CALENDAR_UNITS) {
    const named = unit(from, to, asAtDay, basis);
    if (named) return named;
  }

  return rangeText(from, to, basis);
}
